import { Box, Button, Chip, IconButton, Stack, Tab, TabList, Tabs, Tooltip, Typography, tabClasses, CircularProgress } from '@mui/joy';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router';
import { motion } from 'framer-motion';

import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AdNotificationsBell from '../../../components/Ads/AdNotificationsBell';

import { useAdvertiser } from '../../../AdvertiserContext';


const SUB_ROUTES = {
    campaigns: '/business',
    reports: '/business/reports',
    forecast: '/business/forecast',
    rules: '/business/rules',
    billing: '/business/billing',
};


export default function AdsLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { advertiser, authStatus, logout } = useAdvertiser();

    // Гард кабинета: без токена — на вход, без подтверждённого email — объяснение.
    if (authStatus === 'loading') {
        return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>;
    }
    if (authStatus === 'guest') {
        return <Navigate to="/business/login" replace />;
    }
    if (authStatus === 'unverified') {
        return (
            <Box sx={{ maxWidth: 480, mx: 'auto', pt: 8, px: 2, textAlign: 'center' }}>
                <Typography level="h4" sx={{ mb: 1 }}>Подтвердите email</Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                    Мы отправили ссылку подтверждения на вашу почту. Откройте её, чтобы войти в кабинет.
                </Typography>
                <Button variant="outlined" color="neutral"
                        onClick={() => { logout(); navigate('/business/login'); }}>
                    Выйти
                </Button>
            </Box>
        );
    }

    const path = location.pathname;
    const value = path.startsWith('/business/billing')
        ? 'billing'
        : path.startsWith('/business/reports')
            ? 'reports'
            : path.startsWith('/business/forecast')
                ? 'forecast'
                : path.startsWith('/business/rules')
                    ? 'rules'
                    : 'campaigns';

    return (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}>
            <Box sx={{ px: { xs: 2, md: 4 }, pt: 2, pb: 6, maxWidth: 1200, mx: 'auto' }}>
                <Stack direction="row" spacing={1.5} alignItems="center"
                       justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{
                            width: 44, height: 44, borderRadius: 'md',
                            bgcolor: 'primary.softBg', color: 'primary.700',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <CampaignRoundedIcon />
                        </Box>
                        <Box>
                            <Typography level="h3" component="h1">Рекламный кабинет</Typography>
                            <Typography level="body-sm" color="neutral" sx={{ mt: 0.3 }}>
                                {advertiser?.company_name || advertiser?.email || ''}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AdNotificationsBell />
                        <Stack direction="row" spacing={1} alignItems="center">
                            <AdNotificationsBell />
                            <Tooltip title="Выйти" arrow>
                                <IconButton variant="soft" color="neutral"
                                            onClick={() => { logout(); navigate('/business/login'); }}>
                                    <LogoutRoundedIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </Stack>

                <Tabs value={value} onChange={(_, v) => navigate(SUB_ROUTES[v] || '/business')}
                      sx={{ bgcolor: 'transparent', mb: 2 }}>
                    <TabList variant="soft" sx={{
                        borderRadius: 'md',
                        [`& .${tabClasses.root}.${tabClasses.selected}`]: {
                            bgcolor: 'primary.softBg', color: 'primary.700',
                        },
                    }}>
                        <Tab value="campaigns">Кампании</Tab>
                        <Tab value="reports">Статистика</Tab>
                        <Tab value="forecast">Прогноз</Tab>
                        <Tab value="rules">Автоправила</Tab>
                        <Tab value="billing">Баланс и оплата</Tab>
                    </TabList>
                </Tabs>

                <Outlet />
            </Box>
        </motion.div>
    );
}